import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { useAuth } from '../../../contexts/AuthContext';

const RegistrationPanel = ({ showLoginForm, setShowLoginForm }) => {
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async (e) => {
    e?.preventDefault();
    setError('');
    setLoading(true);

    try {
      const { data, error: signInError } = await signIn(email, password);
      if (signInError) {
        setError(signInError?.message || 'Login failed');
      } else {
        navigate('/student-dashboard');
      }
    } catch (err) {
      setError('An error occurred during login');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-card border border-border rounded-xl p-6 shadow-lg sticky top-20">
      {!showLoginForm ? (
        <>
          {/* Registration CTA */}
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="UserPlus" size={32} color="var(--color-primary)" />
            </div>
            <h2 className="text-2xl font-heading font-bold text-foreground mb-2">
              Start Your Journey
            </h2>
            <p className="text-muted-foreground text-sm">
              Register now and get instant access to 500+ institutions
            </p>
          </div>

          {/* Benefits List */}
          <div className="space-y-3 mb-6">
            <div className="flex items-start gap-3">
              <div className="w-5 h-5 bg-success/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <Icon name="Check" size={14} color="var(--color-success)" />
              </div>
              <p className="text-sm text-foreground">Free registration & unlimited access</p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-5 h-5 bg-success/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <Icon name="Check" size={14} color="var(--color-success)" />
              </div>
              <p className="text-sm text-foreground">Live video counseling sessions</p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-5 h-5 bg-success/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <Icon name="Check" size={14} color="var(--color-success)" />
              </div>
              <p className="text-sm text-foreground">360° virtual campus tours</p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-5 h-5 bg-success/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <Icon name="Check" size={14} color="var(--color-success)" />
              </div>
              <p className="text-sm text-foreground">Instant chat with institute reps</p>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="space-y-3">
            <Button 
              fullWidth 
              size="lg"
              onClick={() => navigate('/student-registration')}
              iconName="ArrowRight"
              iconPosition="right"
            >
              Register Now - It's Free
            </Button>
            <Button 
              fullWidth 
              size="lg" 
              variant="outline"
              onClick={() => setShowLoginForm(true)}
            >
              Already Registered? Sign In
            </Button>
          </div>

          {/* Social Proof */}
          <div className="mt-6 pt-6 border-t border-border">
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Icon name="Users" size={16} />
              <span>Join 50,000+ students already registered</span>
            </div>
          </div>
        </>
      ) : (
        <>
          {/* Login Form */}
          <div className="mb-6">
            <button
              onClick={() => setShowLoginForm(false)}
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-smooth mb-4"
            >
              <Icon name="ArrowLeft" size={16} />
              <span className="text-sm">Back</span>
            </button>
            <h2 className="text-2xl font-heading font-bold text-foreground mb-2">
              Welcome Back
            </h2>
            <p className="text-muted-foreground text-sm">
              Sign in to continue your journey
            </p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            {error && (
              <div className="bg-error/10 border border-error text-error px-4 py-3 rounded-lg flex items-center gap-2 text-sm">
                <Icon name="AlertCircle" size={16} />
                <span>{error}</span>
              </div>
            )}

            <Input
              type="email"
              label="Email Address"
              placeholder="your.email@example.com"
              value={email}
              onChange={(e) => setEmail(e?.target?.value)}
              required
            />

            <Input
              type="password"
              label="Password"
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e?.target?.value)}
              required
            />

            <Button 
              type="submit" 
              fullWidth 
              size="lg"
              loading={loading}
              disabled={loading}
            >
              Sign In
            </Button>

            <div className="text-center">
              <button
                type="button"
                onClick={() => navigate('/student-registration')}
                className="text-sm text-primary hover:underline"
              >
                Don't have an account? Register now
              </button>
            </div>
          </form>
        </>
      )}
    </div>
  );
};

export default RegistrationPanel;